import {Trans, useTranslation} from 'react-i18next'
import {LanguageCombination, LANGUAGES_SHOWCASE_COMBINATIONS} from './utils'
import {useMemo} from 'react'
import {localizedPaths} from '~/utils/routes'
import {SupportedLanguage} from '~/utils/locales'
import {Link, useLocation} from 'react-router'
import {TLink} from '~/features/TLink'

const formatPath = (path: string) => path.split('/').pop()?.replace(/-/g, ' ')

export const LanguagesShowcase = () => {
  const {t, i18n} = useTranslation()
  const location = useLocation()

  // We're using paths as opposed to translated keys since it's currently the only place where
  // we have fully SEO-optimized keywords. For example, the italian nav label is "Traduci"
  // (non optimized), instead of "Traduttore" (optimized).
  const keywords = useMemo(() => {
    const translatePaths = {...localizedPaths['/translate']}
    const filteredTranslatePaths = Object.fromEntries(
      Object.entries(translatePaths).filter(([key]) =>
        i18n.language === 'id' ? key !== 'it' : key !== 'id'
      )
    )
    return Object.values(filteredTranslatePaths)
  }, [i18n.language])

  // We're using multiple flex columns instead of grid since, in this case, it gives more freedom
  // for the desired implementation of the layout.
  const keywordsColumns = useMemo(() => {
    const numColumns = 5
    const itemsPerColumn = Math.ceil(keywords.length / numColumns)
    const columns = []

    for (let i = 0; i < numColumns; i++) {
      columns.push(keywords.slice(i * itemsPerColumn, (i + 1) * itemsPerColumn))
    }

    return columns
  }, [keywords])

  // The combinations must follow a specific displaying logic.
  // "current => en", and "en => current" should always be included in the list. If not present, we add them.
  // We then pick the {n} remaining ones from the original combinations array.
  const showcaseCombinations = useMemo(() => {
    const currentLanguage = i18n.language as SupportedLanguage

    if (currentLanguage === 'en') return LANGUAGES_SHOWCASE_COMBINATIONS

    const currentToEn: LanguageCombination = {source: currentLanguage, target: 'en'}
    const enToCurrent: LanguageCombination = {source: 'en', target: currentLanguage}

    const combinations = [...LANGUAGES_SHOWCASE_COMBINATIONS]

    const hasCurrentToEn = combinations.some(
      comb => comb.source === currentLanguage && comb.target === 'en'
    )
    const hasEnToCurrent = combinations.some(
      comb => comb.source === 'en' && comb.target === currentLanguage
    )

    if (!hasCurrentToEn) combinations.unshift(currentToEn)
    if (!hasEnToCurrent) combinations.unshift(enToCurrent)

    return combinations.slice(0, 6)
  }, [i18n.language])

  return (
    <section className="flex justify-center bg-[#DBE9FF]">
      <div className="flex w-full max-w-[1290px] flex-col gap-12 font-medium text-[#29292d] lg:flex-row lg:gap-0">
        <div className="flex flex-col gap-4 lg:w-[63%] lg:pe-6 lg:ps-1">
          <div className="flex flex-wrap gap-4 text-sm sm:hidden">
            {keywords.map(path => (
              <Link
                key={path}
                to={encodeURIComponent(path.replace(/^\//, ''))}
                reloadDocument
                className="me-5 capitalize"
              >
                {formatPath(path)}
              </Link>
            ))}
          </div>
          <div className="hidden flex-row justify-between text-sm sm:flex sm:gap-2 lg:gap-7">
            {keywordsColumns.map((column, columnIndex) => (
              <div key={columnIndex} className="flex min-w-fit flex-col gap-2">
                {column.map(path => (
                  <Link
                    key={path}
                    to={{pathname: path, search: location.search}}
                    reloadDocument
                    className="me-5 capitalize"
                  >
                    {formatPath(path)}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 text-sm sm:grid sm:grid-cols-3 lg:ms-auto lg:w-[23.5%] lg:grid-cols-1 lg:gap-2 lg:pe-6 lg:ps-[18px]">
          {showcaseCombinations.map(({source, target}, index) => (
            <TLink
              key={index}
              className="w-fit font-regular"
              to="/translate"
              queryParams={{source, target}}
            >
              <Trans
                i18nKey="languages_showcase.language_to_language"
                values={{startLang: t('languages.' + source), endLang: t('languages.' + target)}}
                components={{span: <span className="font-medium" />}}
              />
            </TLink>
          ))}
        </div>
      </div>
    </section>
  )
}
